import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Globe, Store } from "lucide-react";
import { isMhqUser } from "@/lib/permissions";

// A dealer's website only exists to link to once a site has actually been
// deployed — "no_site" and "site_deleted" both mean there's nothing to open.
// "disabled" still means a deployed site exists (just paused), so it stays here too.
const WEBSITE_LIVE_STATUSES = new Set(["offline", "online", "disabled"]);

function cityOf(b) {
  return (b?.city && (b.city["de-DE"] || Object.values(b.city)[0])) || "";
}

export default function MyDealerships() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    // This page is for Dealer users only — an MHQ user has the full /dealers
    // list instead, which is the more capable equivalent for them.
    if (isMhqUser(user)) {
      navigate("/dealers", { replace: true });
      return;
    }
    let ignore = false;
    client
      .get("/dealers", { params: { all: true } })
      .then(({ data }) => {
        if (!ignore) setItems(data.items || []);
      })
      .catch(() => {
        if (!ignore) setItems([]);
      });
    return () => {
      ignore = true;
    };
  }, [user, navigate]);

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-10" data-testid="my-dealerships-page">
      <div className="mb-6">
        <p className="data-label mb-2">{t("nav.myDealerships")}</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("myDealerships.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("myDealerships.subtitle")}</p>
      </div>

      {!items && <Skeleton className="h-64" />}

      {items && items.length === 0 && (
        <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="my-dealerships-empty">
          <Store className="mx-auto mb-3 h-8 w-8" strokeWidth={1.5} />
          {t("myDealerships.empty")}
        </Card>
      )}

      {items && items.length > 0 && (
        <Card className="overflow-hidden p-0">
          <table className="min-w-full text-sm" data-testid="my-dealerships-table">
            <thead className="thead">
              <tr>
                <th className="px-4 py-2 text-left">{t("dealers.columns.code")}</th>
                <th className="px-4 py-2 text-left">{t("dealers.columns.name")}</th>
                <th className="px-4 py-2 text-left">{t("dealers.columns.city")}</th>
                <th className="px-4 py-2 text-right">{t("myDealerships.columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const branch = d.primary_branch || {};
                const status = d.cms_site_status || "no_site";
                const websiteUrl = d.website_url;
                const canVisitWebsite = WEBSITE_LIVE_STATUSES.has(status) && !!websiteUrl;
                return (
                  <tr key={d.id} className="border-t border-border" data-testid={`my-dealership-row-${d.id}`}>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs tabular-nums">{d.global_id}</td>
                    <td className="px-4 py-2 font-medium">
                      <Link to={`/my-dealerships/${d.id}`} className="hover:underline">
                        {d.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{cityOf(branch)}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {canVisitWebsite ? (
                          <a
                            href={websiteUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={t("myDealerships.visitWebsite")}
                            data-testid={`visit-website-btn-${d.id}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          >
                            <Globe className="h-3.5 w-3.5" />
                          </a>
                        ) : (
                          <span
                            title={t("myDealerships.noWebsiteYet")}
                            data-testid={`visit-website-btn-${d.id}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground/30"
                          >
                            <Globe className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`dealer-actions-btn-${d.id}`}
                              aria-label={t("myDealerships.columns.actions")}
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/my-dealerships/${d.id}`)}
                              data-testid={`view-details-btn-${d.id}`}
                            >
                              {t("myDealerships.viewDetails")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
